import React, { useState, useEffect } from 'react';
import { dataSourceAPI } from '../lib/api';
import { formatDate, formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus,
  RefreshCw,
  Database,
  Building,
  Smartphone,
  Ship,
  FileText,
  Shield,
  Bitcoin,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  Landmark,
  Home,
  Gamepad2,
  ArrowLeftRight,
  Wallet,
  MoreHorizontal,
  Activity,
  Zap,
  Radio,
} from 'lucide-react';
import { toast } from 'sonner';

const DATA_SOURCE_TYPES = [
  { value: 'bank', label: 'Bank', icon: Building, color: 'from-blue-500 to-blue-600' },
  { value: 'mobile_network', label: 'Mobile Network', icon: Radio, color: 'from-violet-500 to-violet-600' },
  { value: 'payment_gateway', label: 'Payment Gateway', icon: CreditCard, color: 'from-purple-500 to-purple-600' },
  { value: 'payment_service_provider', label: 'Payment Service Provider', icon: Wallet, color: 'from-indigo-500 to-indigo-600' },
  { value: 'regulator', label: 'Regulator', icon: Landmark, color: 'from-amber-500 to-amber-600' },
  { value: 'trade_customs', label: 'Trade & Customs', icon: Ship, color: 'from-cyan-500 to-cyan-600' },
  { value: 'corporate_registry', label: 'Corporate Registry', icon: FileText, color: 'from-slate-500 to-slate-600' },
  { value: 'watchlist', label: 'Watchlist', icon: Shield, color: 'from-red-500 to-red-600' },
  { value: 'crypto_exchange', label: 'Crypto Exchange', icon: Bitcoin, color: 'from-orange-500 to-orange-600' },
  { value: 'insurance', label: 'Insurance', icon: Shield, color: 'from-teal-500 to-teal-600' },
  { value: 'securities', label: 'Securities & Capital Markets', icon: ArrowLeftRight, color: 'from-pink-500 to-pink-600' },
  { value: 'real_estate', label: 'Real Estate', icon: Home, color: 'from-emerald-500 to-emerald-600' },
  { value: 'gaming_lottery', label: 'Gaming & Lottery', icon: Gamepad2, color: 'from-violet-500 to-violet-600' },
  { value: 'remittance', label: 'Remittance Services', icon: ArrowLeftRight, color: 'from-sky-500 to-sky-600' },
  { value: 'fintech', label: 'Fintech', icon: Wallet, color: 'from-fuchsia-500 to-fuchsia-600' },
  { value: 'other', label: 'Other Sources', icon: MoreHorizontal, color: 'from-gray-500 to-gray-600' },
];

export default function DataSources() {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [healthResults, setHealthResults] = useState({});
  const [newSource, setNewSource] = useState({
    name: '',
    source_type: 'bank',
  });

  useEffect(() => {
    fetchDataSources();
  }, []);

  const fetchDataSources = async () => {
    setLoading(true);
    try {
      const response = await dataSourceAPI.list();
      setDataSources(response.data);
    } catch (error) {
      console.error('Failed to fetch data sources:', error);
      toast.error('Failed to load data sources');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSource = async (e) => {
    e.preventDefault();
    try {
      await dataSourceAPI.create(newSource);
      toast.success('Data source added successfully');
      setShowCreateModal(false);
      setNewSource({ name: '', source_type: 'bank' });
      fetchDataSources();
    } catch (error) {
      toast.error('Failed to add data source');
    }
  };

  const handleCheckAllHealth = async () => {
    setCheckingHealth(true);
    try {
      const response = await dataSourceAPI.checkAllHealth();
      const results = {};
      response.data.sources.forEach(s => {
        results[s.source_id] = s;
      });
      setHealthResults(results);
      toast.success(`Health check complete: ${response.data.healthy}/${response.data.total} healthy`);
    } catch (error) {
      toast.error('Failed to check health');
    } finally {
      setCheckingHealth(false);
    }
  };

  const handleCheckSingleHealth = async (sourceId) => {
    try {
      const response = await dataSourceAPI.checkHealth(sourceId);
      setHealthResults(prev => ({
        ...prev,
        [sourceId]: response.data
      }));
      toast.success(`${response.data.status === 'healthy' ? '✓' : '✗'} ${response.data.source_name}: ${response.data.status}`);
    } catch (error) {
      toast.error('Failed to check health');
    }
  };

  const getSourceIcon = (type) => {
    const source = DATA_SOURCE_TYPES.find((s) => s.value === type);
    return source ? source.icon : Database;
  };

  const getSourceColor = (type) => {
    const source = DATA_SOURCE_TYPES.find((s) => s.value === type);
    return source ? source.color : 'from-gray-500 to-gray-600';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-400 border-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-400 border-red-400">
            <XCircle className="w-3 h-3 mr-1" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-[10px] bg-yellow-500/10 text-yellow-400 border-yellow-400">
            <AlertCircle className="w-3 h-3 mr-1" />
            Inactive
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="data-sources-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Data Sources
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure and monitor data ingestion feeds
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2" 
            onClick={handleCheckAllHealth}
            disabled={checkingHealth}
            data-testid="check-health-btn"
          >
            {checkingHealth ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Activity className="w-4 h-4" />
            )}
            Check All Health
          </Button>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-source-btn">
                <Plus className="w-4 h-4" />
                Add Source
              </Button>
            </DialogTrigger>
          <DialogContent className="glass border-border">
            <DialogHeader>
              <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
                Add Data Source
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSource} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="label-mono">Source Name</Label>
                <Input
                  value={newSource.name}
                  onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                  placeholder="e.g., First Bank Nigeria"
                  className="bg-black/20 border-white/10"
                  required
                  data-testid="source-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Source Type</Label>
                <Select
                  value={newSource.source_type}
                  onValueChange={(value) => setNewSource({ ...newSource, source_type: value })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="source-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {DATA_SOURCE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-source-btn">
                  Add Source
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Source Type Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {DATA_SOURCE_TYPES.slice(0, 8).map((type) => {
          const count = dataSources.filter((s) => s.source_type === type.value).length;
          return (
            <Card key={type.value} className="glass card-hover">
              <CardContent className="p-3">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center shadow-lg`}>
                    <type.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-secondary text-xl font-bold text-white">{count}</p>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider leading-tight">
                      {type.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Additional Source Types */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {DATA_SOURCE_TYPES.slice(8).map((type) => {
          const count = dataSources.filter((s) => s.source_type === type.value).length;
          return (
            <Card key={type.value} className="glass card-hover">
              <CardContent className="p-3">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center shadow-lg`}>
                    <type.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-secondary text-xl font-bold text-white">{count}</p>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider leading-tight">
                      {type.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Data Sources List */}
      <Card className="glass">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
            Connected Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading data sources...</p>
            </div>
          ) : dataSources.length > 0 ? (
            <div className="divide-y divide-white/5">
              {dataSources.map((source) => {
                const SourceIcon = getSourceIcon(source.source_type);
                const sourceColor = getSourceColor(source.source_type);
                const healthInfo = healthResults[source.id];
                return (
                  <div
                    key={source.id}
                    className="p-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${sourceColor} flex items-center justify-center shadow-lg`}>
                      <SourceIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-white">{source.name}</h4>
                        {getStatusBadge(source.connection_status)}
                        {healthInfo && (
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] ${
                              healthInfo.status === 'healthy' 
                                ? 'bg-green-500/10 text-green-400 border-green-400' 
                                : 'bg-red-500/10 text-red-400 border-red-400'
                            }`}
                          >
                            <Zap className="w-3 h-3 mr-1" />
                            {healthInfo.latency_ms ? `${healthInfo.latency_ms}ms` : 'Offline'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Type: {DATA_SOURCE_TYPES.find((t) => t.value === source.source_type)?.label}
                      </p>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div>
                        <p className="font-mono text-white">
                          {formatNumber(source.records_count)} records
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Last sync: {source.last_sync ? formatDate(source.last_sync) : 'Never'}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleCheckSingleHealth(source.id)}
                        title="Check health"
                      >
                        <Activity className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Database className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No data sources configured</p>
              <p className="text-sm text-muted-foreground/50 mt-1">
                Add a data source to start ingesting data
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
